import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  EXPORT_REGISTRATIONS,
  REGISTRATION_EXPORT_STATUS,
  GET_REGISTRATION_EXPORT_DOWNLOAD_URL,
} from '../gql/exportRegistrations';

const useRegistrationsExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const subscriptionRef = useRef(null);

  const client = useApolloClient();

  // Export mutation
  const [exportRegistrationsMutation] = useMutation(EXPORT_REGISTRATIONS);

  // Start export function
  const startExport = useCallback(
    async (eventId, filters = {}, format = 'CSV') => {
      try {
        setIsExporting(true);

        // Get user's timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Build the input object with format and timezone
        const input = {
          event_id: eventId,
          format: format.toLowerCase(), // "csv" or "pdf"
          timezone,
        };

        // Add filters if they exist
        if (filters.search) {
          input.search = filters.search;
        }
        if (filters.ticket_id) {
          input.ticket_id = filters.ticket_id;
        }
        if (filters.division) {
          input.division = filters.division;
        }

        // Execute the mutation
        const { data } = await exportRegistrationsMutation({
          variables: {
            input,
          },
        });

        if (data?.getRegistrationsReport?.jobId) {
          setCurrentJobId(data.getRegistrationsReport.jobId);
          return data.getRegistrationsReport.jobId;
        } else {
          throw new Error('No job ID returned from export');
        }
      } catch (error) {
        setIsExporting(false);
        throw error;
      }
    },
    [exportRegistrationsMutation]
  );

  // Subscribe to export progress
  const subscribeToProgress = useCallback(
    (jobId, callbacks = {}) => {
      const { onComplete, onError, onProgress } = callbacks;

      // Clean up any existing subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }

      // Create new subscription
      subscriptionRef.current = client
        .subscribe({
          query: REGISTRATION_EXPORT_STATUS,
          variables: { jobId },
        })
        .subscribe({
          next: ({ data }) => {
            const status = data?.registrationsExportStatus;

            if (!status) return;

            // Call progress callback if provided
            if (onProgress && status.progress !== null) {
              onProgress(status);
            }

            // Handle completion
            if (status.status === 'COMPLETED') {
              setIsExporting(false);
              if (onComplete) {
                onComplete(status);
              }
              // Clean up subscription after completion
              if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
              }
            }

            // Handle failure
            if (status.status === 'FAILED') {
              setIsExporting(false);
              if (onError) {
                onError(new Error(status.error || 'Export failed'));
              }
              // Clean up subscription after failure
              if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
              }
            }
          },
          error: (error) => {
            setIsExporting(false);
            if (onError) {
              onError(error);
            }
            // Clean up subscription on error
            if (subscriptionRef.current) {
              subscriptionRef.current.unsubscribe();
              subscriptionRef.current = null;
            }
          },
        });

      return subscriptionRef.current;
    },
    [client]
  );

  // Get download URL
  const getDownloadUrl = useCallback(
    async (jobId) => {
      try {
        const { data } = await client.query({
          query: GET_REGISTRATION_EXPORT_DOWNLOAD_URL,
          variables: { jobId: jobId || currentJobId },
          fetchPolicy: 'network-only', // Always get fresh URL
        });

        return data?.getRegistrationsExportDownloadUrl;
      } catch (error) {
        console.error('Failed to get download URL:', error);
        throw error;
      }
    },
    [client, currentJobId]
  );

  // Clean up subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  return {
    startExport,
    subscribeToProgress,
    getDownloadUrl,
    isExporting,
    currentJobId,
  };
};

export default useRegistrationsExport;
